import { useTranslation } from "react-i18next";
import { apiUrl } from "../../config/environment";
import { useState, type FormEvent } from "react";
import { Paperclip, Save, Upload } from "lucide-react";
import type { Feature, Field, Lookups, Row } from "../../config/types";
import { api, saveRecord, uploadFile } from "../../services/api";
import { today, localTime } from "../../utils/format";
import { ErrorNotice, Modal } from "./Primitives";
import StyledSelect, { type SelectOption } from "./StyledSelect";
import EmployeeParticipants from "./EmployeeParticipants";
import EmployeeLoginInvitation from "./EmployeeLoginInvitation";
import ManagedDepartments from "./ManagedDepartments";
import MultiSelectField from "./MultiSelectField";
export default function RecordForm({
  feature,
  row,
  repeat,
  routing,
  initialValues,
  lookups,
  onClose,
  onSaved,
}: {
  feature: Feature;
  row?: Row;
  repeat?: boolean;
  routing?: boolean;
  initialValues?: Record<string, unknown>;
  lookups: Lookups;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const fields = routing
    ? feature.fields.filter((f) =>
        ["departmentId", "employeeId"].includes(f.key),
      )
    : feature.fields;
  const displayedFields = feature.key === "employees"
    ? [
        ...fields.filter((field) => field.type !== "checkbox"),
        ...fields.filter((field) => field.type === "checkbox"),
      ]
    : fields;
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(
      fields.map((f) => [
        f.key,
        repeat && ["date", "timeIn", "timeOut"].includes(f.key)
          ? f.key === "date"
            ? today()
            : f.key === "timeIn"
              ? localTime()
              : ""
          : (feature.key === "visits" && ["departmentIds", "employeeIds"].includes(f.key)
            ? (Array.isArray(row?.[f.key]) && (row![f.key] as string[]).length ? row![f.key] : row?.[f.key === "departmentIds" ? "departmentId" : "employeeId"] ? [row[f.key === "departmentIds" ? "departmentId" : "employeeId"]] : [])
            : row?.[f.key] ??
            initialValues?.[f.key] ??
            (["participants", "managedDepartments"].includes(f.type || "") ? [] : f.type === "date" && ["date", "receivedDate"].includes(f.key)
              ? today()
              : f.key === "timeIn"
                ? localTime()
                : f.type === "checkbox"
                  ? f.key === "active"
                  : "")),
      ]),
    ),
  );
  const [file, setFile] = useState<File>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachment, setAttachment] = useState<string | null>(
    (row?.attachmentId as string) || null,
  );
  function change(key: string, value: unknown) {
    setValues((v) => ({
      ...v,
      [key]: value,
      ...(key === "departmentIds" ? {
        employeeIds: (Array.isArray(v.employeeIds) ? v.employeeIds as string[] : []).filter(id => {
          const employee = lookups.employees.find(item => item.id === id);
          return employee && (value as string[]).some(departmentId => employee.departmentId === departmentId || (Array.isArray(employee.managedDepartmentIds) && employee.managedDepartmentIds.includes(departmentId)));
        }),
      } : {}),
      ...(key === "departmentId"
        ? {
            ...(fields.some((field) => field.key === "employeeId")
              ? { employeeId: "" }
              : {}),
            ...(feature.key === "interviews" ? { technicalManager: "" } : {}),
          }
        : {}),
      ...(key === "floorId" && feature.key === "meeting-reservations"
        ? { roomId: "" }
        : {}),
    }));
  }
  function optionsFor(field: Field): SelectOption[] {
    const current = String(values[field.key] || "");
    const options: SelectOption[] = [];
    if (
      row &&
      field.type === "select" &&
      current &&
      !field.options?.includes(current)
    )
      options.push({ value: current, label: current });
    if (field.type === "employeeName") {
      if (current) options.push({ value: current, label: current });
      options.push(
        ...lookups.employees
          .filter(
            (employee) =>
              employee.fullName !== current &&
              (employee.departmentId === values.departmentId ||
                (field.key === "technicalManager" && Array.isArray(employee.managedDepartmentIds) && employee.managedDepartmentIds.includes(String(values.departmentId)))) &&
              employee.active !== false,
          )
          .map((employee) => ({
            value: String(employee.fullName),
            label: String(employee.fullName),
          })),
      );
    }
    if (field.type === "rhUser")
      options.push(
        ...lookups.users
          .filter((user) => user.role === "RH")
          .map((user) => ({
            value: String(user.fullName),
            label: String(user.fullName),
          })),
      );
    if (field.type === "directionUser")
      options.push(
        ...lookups.users
          .filter((user) => {
            const department = lookups.departments.find(
              (item) => item.id === user.departmentId,
            );
            return (
              department?.name === "Executive Management" ||
              department?.nameFr === "Direction générale"
            );
          })
          .map((user) => ({
            value: String(user.fullName),
            label: String(user.fullName),
          })),
      );
    if (field.type === "floor")
      options.push(
        ...lookups.floors
          .filter((floor) => floor.active || floor.id === values[field.key])
          .map((floor) => ({
            value: String(floor.id),
            label: String(
              i18n.resolvedLanguage === "fr"
                ? floor.nameFr
                : floor.nameEn || floor.name,
            ),
          })),
      );
    if (field.type === "meetingRoom")
      options.push(
        ...lookups.meetingRooms
          .filter(
            (room) =>
              room.floorId === values.floorId &&
              (room.active || room.id === values[field.key]),
          )
          .map((room) => ({
            value: String(room.id),
            label: String(room.name),
          })),
      );
    if (field.type === "department")
      options.push(
        ...lookups.departments.map((department) => ({
          value: String(department.id),
          label: String(
            i18n.resolvedLanguage === "fr"
              ? department.nameFr
              : department.nameEn || department.name,
          ),
        })),
      );
    if (field.type === "role") {
      if (
        current &&
        !lookups.roles.some((role) => role.code === current)
      )
        options.push({ value: current, label: current });
      options.push(
        ...lookups.roles.map((role) => ({
          value: String(role.code),
          label: String(role.name),
        })),
      );
    }
    if (field.type === "employee")
      options.push(
        ...lookups.employees
          .filter(
            (employee) =>
              Boolean(values.departmentId) &&
              employee.departmentId === values.departmentId &&
              employee.active !== false,
          )
          .map((employee) => ({
            value: String(employee.id),
            label: String(employee.fullName),
          })),
      );
    if (field.options)
      options.push(
        ...field.options.map((option) => ({
          value: option,
          label: t(option),
        })),
      );
    return Array.from(
      new Map(options.map((option) => [option.value, option])).values(),
    );
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (feature.key === "visits" && (!(values.departmentIds as string[]).length || !(values.employeeIds as string[]).length)) {
      setError("Select at least one department and one recipient.");
      return;
    }
    setBusy(true);
    try {
      const data = Object.fromEntries(
        fields.map((field) => [field.key, values[field.key]]),
      );
      for (const f of fields) {
        if (f.type === "password" && !data[f.key]) delete data[f.key];
        else if (
          !data[f.key] &&
          ["date", "time", "employee", "department"].includes(f.type || "")
        )
          data[f.key] = null;
      }
      let id = attachment;
      if (file) {
        id = (await uploadFile(file, feature.key)).id;
        setAttachment(id);
        setFile(undefined);
      }
      if (feature.attachment && !routing) data.attachmentId = id;
      if (routing)
        await api("/correspondence/" + row!.id + "/route", {
          method: "POST",
          body: JSON.stringify(data),
        });
      else
        await saveRecord("/" + feature.key, data, repeat ? undefined : row?.id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={
        routing
          ? t("Route correspondence")
          : repeat
            ? t("Welcome back")
            : t(`${row ? "Edit" : "New"} ${feature.singular}`)
      }
      subtitle={
        routing
          ? t("Route correspondence")
          : repeat
            ? t("Register a new visit with the visitor’s details.")
            : t("Keep the details up to date for your team.")
      }
      wide
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <form onSubmit={submit}>
        <div className="form-body">
          {error && <ErrorNotice message={error} />}
          {feature.key === "employees" && row && <EmployeeLoginInvitation employee={row} />}
          <div className="form-section-title">
            {["users", "employees"].includes(feature.key)
              ? t("ACCOUNT DETAILS")
              : t("RECORD DETAILS")}
          </div>
          <div className="form-grid">
            {displayedFields.map((f) => (
              ["visitDepartments", "visitRecipients"].includes(f.type || "") ? <MultiSelectField
                key={f.key} label={t(f.label)}
                value={Array.isArray(values[f.key]) ? values[f.key] as string[] : []}
                options={f.type === "visitDepartments" ? lookups.departments.map(department => ({ value: department.id, label: String(i18n.resolvedLanguage === "fr" ? department.nameFr : department.nameEn || department.name) }))
                  : lookups.employees.filter(employee => employee.active !== false && (values.departmentIds as string[]).some(id => employee.departmentId === id || (Array.isArray(employee.managedDepartmentIds) && employee.managedDepartmentIds.includes(id)))).map(employee => ({ value: employee.id, label: String(employee.fullName) }))}
                onChange={value => change(f.key, value)}
                disabled={f.type === "visitRecipients" && !(values.departmentIds as string[]).length}
                placeholder={t(f.type === "visitDepartments" ? "Select a department" : "Select an employee")}
                searchPlaceholder={t(f.type === "visitDepartments" ? "Search departments" : "Search employees")}
              /> : f.type === "managedDepartments" ? <ManagedDepartments
                key={f.key}
                departments={lookups.departments}
                value={Array.isArray(values[f.key]) ? values[f.key] as string[] : []}
                onChange={value => change(f.key, value)}
              /> : f.type === "participants" ? <EmployeeParticipants
                key={f.key}
                employees={lookups.reservationParticipants || []}
                value={Array.isArray(values[f.key]) ? values[f.key] as string[] : []}
                onChange={value => change(f.key, value)}
              /> : <label
                key={f.key}
                className={
                  (f.wide ? "span-two " : "") +
                  (f.type === "checkbox" ? "checkbox-field" : "")
                }
              >
                {f.type !== "checkbox" && (
                  <span>
                    {t(f.label)}
                    {(f.required || (f.key === "password" && !row)) && (
                      <b className="required"> *</b>
                    )}
                  </span>
                )}
                {f.type === "checkbox" ? (
                  <>
                    <input
                      type="checkbox"
                      role="switch"
                      aria-checked={Boolean(values[f.key])}
                      checked={Boolean(values[f.key])}
                      onChange={(e) => change(f.key, e.target.checked)}
                    />
                    <span>{t(f.label)}</span>
                  </>
                ) : [
                    "select",
                    "department",
                    "employee",
                    "personName",
                    "employeeName",
                    "rhUser",
                    "directionUser",
                    "floor",
                    "meetingRoom",
                    "role",
                  ].includes(f.type || "") ? (
                  <StyledSelect
                    label={t(f.label)}
                    required={f.required}
                    value={String(values[f.key] || "")}
                    options={optionsFor(f)}
                    placeholder={t("Select {{field}}", {
                      field: t(f.label).toLowerCase(),
                    })}
                    onChange={(value) => change(f.key, value)}
                    disabled={
                      (["employee", "employeeName"].includes(f.type || "") &&
                        !values.departmentId) ||
                      (f.type === "directionUser" &&
                        !lookups.users.some((user) => {
                          const department = lookups.departments.find(
                            (item) => item.id === user.departmentId,
                          );
                          return (
                            department?.name === "Executive Management" ||
                            department?.nameFr === "Direction générale"
                          );
                        })) ||
                      (f.type === "meetingRoom" &&
                        (values.floorId === "" || values.floorId === null))
                    }
                  />
                ) : (
                  <div className={f.type === "password" ? "password-generator" : undefined}>
                    <input
                    aria-label={t(f.label)}
                    type={
                      f.type === "autoReference"
                        ? "text"
                        : f.type || "text"
                    }
                    autoComplete={
                      f.type === "password" ? "new-password" : "off"
                    }
                    minLength={f.type === "password" ? 12 : undefined}
                    maxLength={f.type === "password" ? 128 : 500}
                    required={f.required || (f.key === "password" && !row)}
                    disabled={f.type === "autoReference"}
                    value={String(values[f.key] ?? "")}
                    onChange={(e) => change(f.key, e.target.value)}
                  />
                  </div>
                )}
              </label>
            ))}
          </div>
          {feature.attachment && !routing && (
            <div className="attachment-field">
              <label>
                <span>
                  <Paperclip size={17} />
                  {t("Supporting document")}
                </span>
                <p>{t("PDF, JPG, PNG or WebP · up to 10 MB")}</p>
                <input
                  className="file-picker-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.size > 10 * 1024 * 1024) {
                      setError("Attachments must be smaller than 10 MB.");
                      e.target.value = "";
                      return;
                    }
                    setFile(f);
                  }}
                />
                <span className="file-picker-control">
                  <span className="file-picker-button">
                    <Upload size={16} />
                    {t("Choose file")}
                  </span>
                  <span className="file-picker-name">
                    {file?.name ||
                      (attachment
                        ? t("File ready")
                        : t("No file selected"))}
                  </span>
                </span>
              </label>
              {attachment && (
                <div className="attachment-current">
                  <a
                    href={apiUrl("/files/" + attachment)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Download current attachment")}
                  </a>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => setAttachment(null)}
                  >
                    {t("Remove")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="button secondary"
            disabled={busy}
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
          <button className="button primary" disabled={busy}>
            <Save size={17} />
            {busy
              ? t("Saving…")
              : routing
                ? t("Send correspondence")
                : t(`Save ${feature.singular}`)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
